/**
 * One line of news under the app's header, written on /admin.
 *
 * Signed-in app screens only — a marketing page is for somebody who has no
 * account to dismiss it with — and never on a running exam or oral session,
 * where a bar that moves the page is a distraction on the clock, nor above the
 * first-visit questions (`onboard`). It sits
 * outside the sticky header on purpose: the header is a fixed h-14 that the
 * exam's own sticky bar comes to rest beneath.
 *
 * Closing it, or following its link, is remembered on the account, so it does
 * not come back on another device.
 */
import { useEffect, useState } from "react";
import { Megaphone, X } from "lucide-react";
import { Link } from "@/components/link";
import { api } from "@/lib/api";
import { useRoute } from "@/lib/router";
import { useI18n } from "@/i18n";
import { useSession } from "@/state/session";
import { linkKind, type LiveAnnouncement } from "@shared/announcement";

/** Where a candidate is on the clock. */
const BUSY = [/^\/exam\//, /^\/practice\//, /^\/set\/[^/]+\/oral$/];

/** A tab left open for days still hears about the next one, without polling. */
const RECHECK_MS = 30 * 60 * 1000;

export function AnnouncementBar() {
  const { status, onboard } = useSession();
  const { locale, t } = useI18n();
  const path = useRoute();
  const [item, setItem] = useState<LiveAnnouncement | null>(null);

  useEffect(() => {
    if (status !== "ready") return;
    let alive = true;
    let checkedAt = 0;
    const load = (): void => {
      checkedAt = Date.now();
      api.announcement(locale).then(
        (r) => alive && setItem(r.announcement),
        () => {},
      );
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible" && Date.now() - checkedAt > RECHECK_MS) load();
    };
    load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [status, locale]);

  // The first-visit questions are the one thing on screen: news above them is a
  // second thing to read, on a phone most of the screen.
  if (!item || onboard || BUSY.some((re) => re.test(path))) return null;

  const dismiss = (): void => {
    setItem(null);
    void api.dismissAnnouncement(item.id).catch(() => {});
  };
  const kind = item.link ? linkKind(item.link) : null;
  const label = item.linkLabel || t("announcement.more");
  const linkClass = "ml-1.5 whitespace-nowrap font-semibold underline underline-offset-2";

  return (
    <div role="status" className="border-b bg-primary/5">
      <div className="mx-auto flex max-w-3xl items-start gap-2 px-4 py-2 text-sm sm:px-5">
        <Megaphone className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        <p className="min-w-0 flex-1">
          {item.text}
          {item.link && kind === "internal" && (
            <Link to={item.link} className={linkClass} onClick={dismiss}>
              {label} →
            </Link>
          )}
          {item.link && kind === "external" && (
            <a href={item.link} target="_blank" rel="noopener noreferrer" className={linkClass} onClick={dismiss}>
              {label} ↗
            </a>
          )}
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("announcement.close")}
          className="-mr-1 shrink-0 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
